import useStory, { span } from './useStory';
import { Explainer } from './GuideParts';

// GUIDE 9'S ANIMATION (1 Nov 2026): one room and its outdoor unit, twice.
// Summer: dots of heat leave the room through the pipe and the outdoor unit
// throws them outside. Winter: the flow turns round; the outdoor unit pulls
// heat from the cold air and the dots flow into the room, which warms. A
// drawing, not to scale. Plays once in view, rests on the winter picture,
// Replay. No numbers in the picture.

const DOTS = [0, 0.2, 0.4, 0.6, 0.8];
// The path heat takes, room -> indoor unit -> pipe -> outdoor unit -> outside.
const PATH = [[70, 120], [110, 62], [196, 62], [236, 120], [300, 120]];

function along(f) {
  const segs = PATH.length - 1;
  const x = Math.max(0, Math.min(0.9999, f)) * segs;
  const i = Math.floor(x); const r = x - i;
  const [ax, ay] = PATH[i]; const [bx, by] = PATH[i + 1];
  return [ax + (bx - ax) * r, ay + (by - ay) * r];
}

export default function HeatFlow({ watch, sources }) {
  const story = useStory(20000);
  const t = story.t;
  const winter = t >= 0.42;
  const flip = span(t, 0.36, 0.48);                  // the season changes
  const summerFlow = span(t, 0.04, 0.36);
  const winterFlow = span(t, 0.5, 0.92);
  const warm = span(t, 0.55, 0.9);                    // the room warms
  const noFire = span(t, 0.8, 0.9);
  const phase = winter ? winterFlow : summerFlow;
  const room = winter
    ? `rgb(${Math.round(239 + 15 * warm)}, ${Math.round(246 - 10 * warm)}, ${Math.round(255 - 40 * warm)})`
    : '#eff6ff';

  return (
    <Explainer title={watch.title} captions={watch.captions} story={story} note={watch.note} noteSrc={watch.noteSrc} sources={sources}>
      <div className="grid gap-2">
        <svg viewBox="0 0 330 200" width="100%" role="img" className="block h-auto rounded-xl border border-gray-200 bg-gray-50"
          aria-label="A room with an AC and its outdoor unit. In summer, heat flows out of the room. In winter, the flow turns round and heat comes into the room from the cold air outside.">
          <rect x="20" y="40" width="190" height="140" fill={room} stroke="#9ca3af" strokeWidth="2" />
          <text x="34" y="170" fontSize="12" fill="#6b7280">Your room</text>
          <line x1="210" x2="210" y1="40" y2="180" stroke="#6b7280" strokeWidth="4" />
          <text x="222" y="170" fontSize="12" fill="#6b7280">Outside</text>
          {/* indoor unit */}
          <rect x="84" y="52" width="70" height="20" rx="5" fill="#fff" stroke="#6b7280" strokeWidth="1.5" />
          {/* pipe through the wall */}
          <path d="M154 62H196V104" fill="none" stroke="#b45309" strokeWidth="3" />
          {/* outdoor unit */}
          <rect x="222" y="98" width="60" height="44" rx="5" fill="#fff" stroke="#6b7280" strokeWidth="1.5" />
          <circle cx="244" cy="120" r="13" fill="none" stroke="#9ca3af" strokeWidth="1.5" />
          {/* season mark: sun, then snowflake */}
          <g opacity={1 - flip}>
            <circle cx="300" cy="24" r="10" fill="#fbbf24" />
          </g>
          <g opacity={flip} stroke="#2563eb" strokeWidth="2" strokeLinecap="round">
            <line x1="300" x2="300" y1="12" y2="36" />
            <line x1="290" x2="310" y1="18" y2="30" />
            <line x1="290" x2="310" y1="30" y2="18" />
          </g>
          {/* heat dots */}
          {DOTS.map((d) => {
            const f = ((phase * 2 + d) % 1);
            const pos = winter ? along(1 - f) : along(f);
            const on = (winter ? winterFlow : summerFlow) > 0 && (winter ? winterFlow : summerFlow) < 1;
            return <circle key={d} cx={pos[0]} cy={pos[1]} r="5" fill="#ea580c" opacity={on ? 0.9 : 0} />;
          })}
          {/* which way heat goes: out in summer, in once winter starts */}
          <g opacity={winter ? warm : 1 - flip} stroke="#ea580c" strokeWidth="2" fill="none" strokeLinecap="round">
            {winter
              ? <path d="M200 132H150M158 125L150 132L158 139" />
              : <path d="M150 132H200M192 125L200 132L192 139" />}
          </g>
          {/* no flame, no fumes */}
          <g opacity={noFire}>
            <rect x="30" y="92" width="112" height="26" rx="13" fill="#fff" stroke="#16a34a" />
            <text x="86" y="109" fontSize="12" fill="#15803d" textAnchor="middle" fontWeight="700">No fire, no fumes</text>
          </g>
        </svg>
        <div className="grid grid-cols-2 gap-2 text-center">
          <div className={`rounded-xl border p-2.5 ${!winter ? 'border-gray-900' : 'border-gray-200'}`}>
            <p className="text-[13px] text-gray-600">Summer</p>
            <p className="text-[18px] font-extrabold text-gray-900">Heat goes out</p>
          </div>
          <div className={`rounded-xl border p-2.5 ${winter ? 'border-blue-600' : 'border-gray-200'}`}>
            <p className="text-[13px] text-gray-600">Winter</p>
            <p className="text-[18px] font-extrabold text-gray-900">Heat comes in</p>
          </div>
        </div>
      </div>
    </Explainer>
  );
}
